import { endOfDay, format, isToday, parseISO } from 'date-fns';
import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';

import type { DeliveryWindow } from 'src/app/types';

/**
 * Format the delivery window time range. Handles all-day category as well.
 *
 * @param from
 * @param to
 * @param {string} category The delivery window category type
 * @return {string}
 */

export function formatDeliveryWindowTimeRange(from: string, to: string, category?: string, facilityTimezone?: string) {
  const timezone = facilityTimezone ? facilityTimezone : 'America/New_York';
  const startTime = moment(from).tz(timezone);
  const endTime = moment(to).tz(timezone);

  // Ensure we show AM and PM when the two times differ.
  const start = startTime.hour() < 12 && endTime.hour() >= 12 ? startTime.format('h A') : startTime.format('h');
  const end = endTime.format('h A');

  const formattedRange = `${start} - ${end}`;

  return category === 'all_day' ? `Any time (${formattedRange})` : formattedRange;
}

/**
 * Format the delivery window for the `/rx/checkout` page.
 * This function gets called, instead of `formatDeliveryWindowTimeRange`, when
 * the DeliveryWindow component is passed a `withDay` props.
 *
 * @param from
 * @param to
 * @param {moment} currentDay an optional argument, used for testing purposes. Defaults to the current day.
 * @return {[string, string]} Tuple of time range parts
 */
export function formatDeliveryWindowTimeRangeWithDay(
  from: string,
  to: string,
  facilityTimezone: string,
  currentDay?: Moment
): [string, string] {
  facilityTimezone = facilityTimezone ?? 'America/New_York';
  const startTime = moment(from).tz(facilityTimezone);
  const endTime = moment(to).tz(facilityTimezone);
  // Ensure we show AM and PM when the two times differ.
  const start = startTime.hour() < 12 && endTime.hour() >= 12 ? startTime.format('h A') : startTime.format('h');
  const end = endTime.format('h A');

  currentDay = currentDay?.tz(facilityTimezone) ?? moment().tz(facilityTimezone);

  const isStartTimeToday = currentDay.isSame(startTime.clone().startOf('day'), 'd');
  const isStartTimeTomorrow = currentDay.isSame(startTime.clone().subtract(1, 'days').startOf('day'), 'd');
  const day = isStartTimeToday ? 'Today' : isStartTimeTomorrow ? 'Tomorrow' : startTime.format('dddd MM/DD,');

  const formattedRange = `${start} - ${end}`;

  return isStartTimeToday || isStartTimeTomorrow ? [formattedRange, day] : [day, formattedRange];
}

/**
 * Formats a postgres date to the format from product.
 *
 * @param {string} postgresDate
 * @return {string}
 */
export const formatPostgresDate = (postgresDate: string) => moment(postgresDate, 'YYYY-MM-DD').format('MM/DD/YYYY');

export const isSameDayWindowAvailable = (availableDeliveryWindows: DeliveryWindow[]): boolean =>
  isToday(parseISO(availableDeliveryWindows?.[0].startDate));

/**
 *
 * @param {string} birthdate Date in MM-dd-yyyy format
 * @returns {string} Date in yyyy-MM-dd format
 */
export const convertToISODate = (birthdate: string): string => birthdate && format(Date.parse(birthdate), 'yyyy-MM-dd');

/**
 * Returns a date after given hours from right now
 * @param hours Number of hours after right now to return
 */
export const getDateOffsetHours = (hours: number) => {
  const date = new Date(Date.now());
  date.setHours(date.getHours() + hours);
  return date;
};

/**
 *  Returns a date after given days from right now.
 * @param days Number of days after right now to return
 * @param isEndOfDay Boolean, optional - if true will return date at end of the day in local time 11:59pm
 */
export const getDateOffsetDays = (days: number, isEndOfDay?: boolean) => {
  const date = new Date(Date.now());
  date.setDate(date.getDate() + days);
  return isEndOfDay ? endOfDay(date) : date;
};

/**
 * Returns a simple date from a formatted date string
 * @param dateString ex: Monday, Feb 26, 2021
 * @returns string ex: Feb 26
 */
export const getSimpleFormattedDate = (date: string) => {
  if (!date) return date;
  return date.split(', ')[1] ?? date;
};
